/**
 * 週報表格（P1-12 頁 14）：倉租明細 ＋ 出入庫摘要 ＋ 入庫批次
 *
 * ⚠️ 呢個元件**只顯示**（數字照 `WeeklyReport` 抄；前端唔准重算 —— `rules §七 F1`）。
 * ⚠️ 手機優先：表格用 `overflow-x: auto` 橫向捲（`ui-spec` §5），唔好逼爆版面。
 */
import { useTranslation } from 'react-i18next';
import { card, hint as hintStyle } from '@coeldery/ui-kit';
import type { WeeklyReport } from '../lib/api';

const mono: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontWeight: 700 };
const n6 = (n: number): string => Number(n ?? 0).toFixed(6);
const n2 = (n: number): string => Number(n ?? 0).toFixed(2);

const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '14px' };
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid var(--color-divider)',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--color-divider)',
  whiteSpace: 'nowrap',
};

/** 表格外殼（標題 ＋ 可橫向捲嘅表） */
function Block({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 800 }}>{title}</h3>
      {hint && <p style={hintStyle}>{hint}</p>}
      <div style={{ overflowX: 'auto' }}>{children}</div>
    </section>
  );
}

export default function ReportTables({ report }: { report: WeeklyReport }) {
  const { t } = useTranslation();
  const f = report.billing;

  return (
    <>
      {/* ① 倉租明細（期初制：期初 cbm × 費率 ＋ 入庫手續費） */}
      <Block title={t('report.feeTitle')} hint={t('report.feeHint')}>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>{t('report.week')}</th>
              <th style={th}>{t('weeklyFee.openingCbm')}</th>
              <th style={th}>{t('weeklyFee.outboundCbm')}</th>
              <th style={th}>{t('weeklyFee.closingCbm')}</th>
              <th style={th}>{t('weeklyFee.storageRate')}</th>
              <th style={th}>{t('weeklyFee.storageAmount')}</th>
              <th style={th}>{t('weeklyFee.handlingAmount')}</th>
              <th style={th}>{t('weeklyFee.totalAmount')}</th>
              <th style={th}>{t('report.status')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={td}>
                {report.weekStart} ~ {report.weekEnd}
              </td>
              <td style={{ ...td, ...mono }}>{n6(f.openingCbm)}</td>
              <td style={{ ...td, ...mono }}>{n6(f.outboundCbm)}</td>
              <td style={{ ...td, ...mono }}>{n6(f.closingCbm)}</td>
              <td style={{ ...td, ...mono }}>${n2(f.storageRate)}</td>
              <td style={{ ...td, ...mono }}>${n2(f.storageAmount)}</td>
              <td style={{ ...td, ...mono }}>${n2(f.handlingAmount)}</td>
              <td style={{ ...td, ...mono }}>${n2(f.totalAmount)}</td>
              {/* ⚠️ 狀態用**機器值**（draft／confirmed）唔翻譯：帳目要同 DB 對得上 */}
              <td style={td}>{report.fee ? report.fee.status : t('report.notGenerated')}</td>
            </tr>
          </tbody>
        </table>
        {report.fee && (
          <p style={hintStyle}>
            {t('report.generatedAt')} {report.fee.generatedAt}
            {'　'}
            {report.matchesGenerated === true && t('report.matchesOk')}
            {report.matchesGenerated === false && t('report.matchesBad')}
          </p>
        )}
      </Block>

      {/* ② 出入庫摘要（帳本；唯一來源 stock_movements） */}
      <Block title={t('report.ledgerTitle')} hint={t('report.ledgerHint')}>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>{t('report.type')}</th>
              <th style={th}>{t('report.count')}</th>
              <th style={th}>{t('report.cartons')}</th>
              <th style={th}>{t('report.units')}</th>
              <th style={th}>{t('report.cbm')}</th>
            </tr>
          </thead>
          <tbody>
            {report.ledger.byType.length === 0 ? (
              <tr>
                <td style={td} colSpan={5}>
                  {t('report.noLedger')}
                </td>
              </tr>
            ) : (
              report.ledger.byType.map((r) => (
                <tr key={r.type}>
                  <td style={td}>{r.type}</td>
                  <td style={{ ...td, ...mono }}>{r.count}</td>
                  <td style={{ ...td, ...mono }}>{r.cartons}</td>
                  <td style={{ ...td, ...mono }}>{r.units}</td>
                  <td style={{ ...td, ...mono }}>{n6(r.cbm)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Block>

      {/* ③ 入庫批次（該週收貨；手續費歸屬週 → 同倉租對得上） */}
      <Block title={t('report.batchTitle')} hint={t('report.batchHint')}>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>{t('report.batchNo')}</th>
              <th style={th}>{t('report.supplier')}</th>
              <th style={th}>{t('report.receivedDate')}</th>
              <th style={th}>{t('report.receivedCbm')}</th>
              <th style={th}>{t('report.handlingFee')}</th>
              <th style={th}>{t('report.status')}</th>
            </tr>
          </thead>
          <tbody>
            {report.intakeBatches.length === 0 ? (
              <tr>
                <td style={td} colSpan={6}>
                  {t('report.noBatch')}
                </td>
              </tr>
            ) : (
              report.intakeBatches.map((b) => (
                <tr key={b.batchNo}>
                  <td style={td}>{b.batchNo}</td>
                  <td style={td}>
                    {b.supplierCode} {b.supplierName}
                  </td>
                  <td style={td}>{b.receivedDate ?? '—'}</td>
                  <td style={{ ...td, ...mono }}>{n6(b.receivedCbm)}</td>
                  <td style={{ ...td, ...mono }}>${n2(b.handlingFee)}</td>
                  <td style={td}>{b.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Block>
    </>
  );
}
